import type { JSX, ReactNode } from 'react';
import type { LegalDocument, LegalRenderContext } from '@kitup/brand';
import { talearooLegal } from './legal-data.js';

/**
 * TaleAroo legal documents.
 *
 * These are NOT translations of the Russian documents: the "Public Offer" is an instrument of
 * Russian law, while the Terms of Service is a different document. The set here reflects the
 * merchant of record scheme, where the reseller is the seller.
 *
 * The texts were written against `docs/north-america-launch-requirements.md` and
 * `docs/abuse-response.md`, and the sections that carry legal weight say so in the comments
 * above them: the parts of a document that do work are not the parts that look important.
 *
 * IMPORTANT: the texts are the work of an engineer, not of a lawyer. Before launch they must
 * be reviewed by a lawyer for the specific jurisdictions and the specific merchant of record.
 */
const { support, privacy, abuse } = talearooLegal;

function Section({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <section>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

/** Addresses are always set, so there is no "configured or not" branch to render. */
function Mail({ address }: { readonly address: string }): JSX.Element {
  return <a href={`mailto:${address}`}>{address}</a>;
}

/**
 * Terms of Service.
 *
 * Three clauses carry the legal weight: the 18+ age gate (it closes the COPPA risk and the
 * "actual knowledge" risk), the statement of parenthood with the prohibition on uploading
 * other people's children (it moves responsibility to the user and gives us a ground to
 * block), and the fact that blocking closes public links — without it a block does not work
 * as a protective measure.
 *
 * What is deliberately ABSENT: mandatory arbitration, class action waivers, bans on publishing
 * reviews, disclaimers of implied warranties, and foreign governing law for a Canadian
 * consumer. BC Bill 4, Ontario CPA 2023 and Quebec Bill 10 make such terms void for a Canadian
 * consumer, and including them means signing a contract that falls apart in court as a whole.
 * The limitation of liability stays, with an express carve-out for consumer law.
 *
 * No promise is made here about our own speed except the one a statute already sets. The 48 hours
 * in "Reporting abuse" is the deadline in 47 U.S.C. § 223a, known identical copies included, so
 * it is a restatement rather than a concession. The acknowledgement deadline, the child-account
 * deadline and the refund-confirmation deadline that used to stand here were ours alone: a
 * stopwatch a single operator has to hit alone, quotable on a bad day. They are internal targets
 * now, in `docs/abuse-procedure.md`. The same reasoning removed a sentence from "Governing law"
 * that promised to sue a user where they live — the carve-out above states what consumer law
 * already does, and a promise to travel to the user's court was a gift with no legal necessity.
 */
function termsOfService({ Link }: LegalRenderContext): ReactNode {
  return (
    <>
      <Section title="The agreement">
        <p>
          These terms are an agreement between you and the operator of {talearooLegal.serviceName}.
          You accept them when you tick the box at registration. The edition you accepted is
          recorded with the date, and it is the edition named at the top of this page.
        </p>
        <p>
          Our <Link to="/legal/privacy">Privacy Policy</Link> explains what we do with your data and
          forms part of this agreement. The{' '}
          <Link to="/legal/payment">Payment and Refund Policy</Link> explains how paying works and
          how to get your money back.
        </p>
      </Section>
      <Section title="What the service does">
        <p>
          {talearooLegal.serviceName} takes photographs of your child, a few details such as their
          first name and age, and a story you choose, and produces an illustrated personalised book.
          Depending on your plan the book can also include animated scenes and spoken narration. The
          illustrations, the scenes and the narration are generated by artificial intelligence.
        </p>
        <p>
          Because the output is generated, it will not be identical for two people who ask for the
          same thing, and it will not always be exactly what you pictured. Likenesses are
          approximate by nature. You can regenerate within your plan’s quota.
        </p>
        <p>
          The service is intended for users in Canada and the United States. We do not market it
          elsewhere, and we may decline to serve users outside those countries.
        </p>
      </Section>
      <Section title="You must be 18 or older">
        <p>
          To hold an account you must be at least 18 years old and able to enter into a binding
          agreement. The service is not for children. Children do not create accounts here, and we
          do not knowingly allow one to be created. If we learn that an account holder is under 18,
          we close the account and delete its data.
        </p>
      </Section>
      <Section title="The photographs you upload">
        <p>This is the most important part of this agreement, and it is short.</p>
        <p>
          By uploading a photograph of a child, you state that you are the parent or the legal
          guardian of that child, and that you have the right to upload that photograph and to have
          it processed as described in our Privacy Policy.
        </p>
        <p>
          You must not upload photographs of anyone else’s child. Not a niece, not a friend’s child,
          not a classmate in a group photo, not a picture found online. This holds even when the
          intention is kind and even when the parent said yes in conversation. If the child is not
          yours to consent for, the photograph does not belong here.
        </p>
        <ul>
          <li>Do not upload photographs of adults other than yourself without their consent.</li>
          <li>
            Do not upload sexual, violent or degrading material, or any material involving the
            sexual exploitation of a child.
          </li>
          <li>
            Do not use the service to impersonate a real person or to create material intended to
            deceive.
          </li>
          <li>
            Do not attempt to bypass quotas, rate limits or the consent check, and do not attack the
            service or the accounts of other users.
          </li>
        </ul>
        <p>
          Material involving the sexual exploitation of a child is reported to the police and to
          Cybertip.ca, and where a user is in the United States, to NCMEC. This is a legal
          obligation, it is not discretionary, and the associated data is preserved for as long as
          the law requires rather than deleted.
        </p>
      </Section>
      <Section title="Who owns what">
        <p>
          You own the photographs and recordings you upload. You keep them; we get only the
          permission we need to run the service for you, which is to store them and to send them to
          the providers listed in the Privacy Policy so that your book can be made.
        </p>
        <p>
          The books, illustrations, video and narration generated for you are yours. You may print
          them, share them, and use them however you like, including commercially. We do not claim
          ownership of them and we do not use them to advertise the service without asking you
          first.
        </p>
        <p>The service itself, its software, its design and its name remain ours.</p>
      </Section>
      <Section title="Payment and renewal">
        <p>
          Paid plans are sold through Polar, which acts as the merchant of record. That means your
          contract of sale for the payment is with Polar, they handle the transaction and any
          applicable tax, and their name is what appears on your statement.
        </p>
        <p>
          Prices are shown before you pay, including any recurring amount and how often it recurs.
          If a plan renews automatically, we say so at checkout, we tell you the renewal amount and
          date, and we send you a reminder before each renewal.
        </p>
        <p>
          You can cancel a subscription at any time, from your account, in a few clicks and without
          contacting us. Cancelling stops future charges. Your access continues until the end of the
          period you have already paid for.
        </p>
        <p>
          Refunds are unconditional. See the{' '}
          <Link to="/legal/payment">Payment and Refund Policy</Link>.
        </p>
        <p>
          If we change our prices, the change applies to renewals after we have notified you, never
          retroactively to a period you have already paid for.
        </p>
      </Section>
      <Section title="Suspension and blocking">
        <p>
          We may suspend or block an account that breaks the rules above, and we may do so
          immediately and without warning where a child may be at risk. In less serious cases we
          will tell you what the problem is and give you a chance to fix it.
        </p>
        <p>
          Blocking an account ends its sessions and closes the public links to its books, so
          published books of a blocked account stop being reachable by anyone.
        </p>
        <p>
          If you think we have blocked you in error, write to <Mail address={support.email} /> and a
          human will look at it. If your account is blocked and you had unused paid time, you can
          ask for a refund of it under the Payment and Refund Policy.
        </p>
      </Section>
      <Section title="Reporting abuse">
        <p>
          If you see a published book that should not exist — a child who is not the account
          holder’s to publish, sexual or exploitative material, or anything else that alarms you —
          write to <Mail address={abuse.email} />. You do not need an account to report something,
          and you can include just the link.
        </p>
        <p>
          We act on abuse reports as they arrive. Content that is reported as an intimate image
          published without the consent of the person shown is removed within 48 hours, together
          with any identical copies we know of.
        </p>
      </Section>
      <Section title="Availability and changes to the service">
        <p>
          We run the service with care, but it is a small service and it may be unavailable at
          times, including for maintenance or because of a failure at one of our providers. We do
          not promise uninterrupted availability.
        </p>
        <p>
          If we discontinue the service, we will give you notice, a period in which to download your
          books, and a refund of any period you have paid for and will not receive.
        </p>
        <p>
          We may update these terms. If a change matters to you, we will notify account holders by
          email before it takes effect, and continued use after that date is acceptance of the new
          edition. A change never applies retroactively to something you have already done or
          already paid for.
        </p>
      </Section>
      <Section title="Our responsibility, and the limits of it">
        <p>
          We are responsible for running the service as described and for handling your data as our
          Privacy Policy says. We are responsible for our own negligence, and nothing in this
          agreement limits our liability for personal injury, for fraud, or for anything else that
          the law does not permit us to limit.
        </p>
        <p>
          Beyond that, our total liability to you for any claim arising from the service is limited
          to the amount you have paid us in the twelve months before the claim, and we are not
          liable for indirect or consequential losses such as lost profits.
        </p>
        <p>
          This section does not take away any right that consumer protection law gives you. If a
          term of this agreement conflicts with a right you have under the consumer protection law
          of your province or state, that law wins and the term does not apply to you. Nothing here
          waives a statutory guarantee, a statutory warranty, or your right to bring a complaint or
          a claim, individually or together with others, in the courts of your own province.
        </p>
      </Section>
      <Section title="Governing law">
        <p>
          This agreement is governed by the laws of the province of Canada in which the operator
          resides and by the federal laws of Canada that apply there.
        </p>
        <p>
          Nothing in this agreement limits your rights as a consumer under the law of the province
          or state where you live. If that law gives you the right to bring a claim in your own
          courts, or protections this agreement cannot lawfully override, those rights are
          preserved.
        </p>
      </Section>
      <Section title="Getting in touch">
        <ul>
          <li>
            Questions about your account, billing or a book: <Mail address={support.email} />
          </li>
          <li>
            Privacy requests, including access, correction, portability and deletion:{' '}
            <Mail address={privacy.email} />
          </li>
          <li>
            Reports of abuse or of content that should not be published:{' '}
            <Mail address={abuse.email} />
          </li>
        </ul>
      </Section>
    </>
  );
}

/**
 * Privacy Policy.
 *
 * Mandatory on four independent grounds at once: PIPEDA principle 8, Quebec Law 25, CalOPPA
 * § 22575 (which has no threshold at all) and the requirement of the merchant of record before
 * it approves the account. So the text is written from what the code actually does rather than
 * from a template: the subprocessors are named, the cross-border transfer is stated outright,
 * and the non-obvious behaviour of a public link — it survives unpublishing — has its own
 * section, because staying silent about it would mislead a parent exactly where the cost of
 * being wrong is highest.
 *
 * Honesty about retention at the provider is mandatory. WaveSpeed publishes no retention
 * period; promising a deletion we cannot perform would make the consent obtained by deception.
 */
function privacyPolicy({ Link }: LegalRenderContext): ReactNode {
  return (
    <>
      <Section title="Who we are">
        <p>
          {talearooLegal.serviceName} is a service that turns photographs of your child into an
          illustrated personalised book, with optional video and narration. The service is operated
          by an individual, not a company, and the operator is personally accountable for the
          personal information described here.
        </p>
        <p>
          You can reach the person responsible for privacy at <Mail address={privacy.email} />. If
          you would rather write about something that is not a privacy matter, use{' '}
          <Mail address={support.email} />.
        </p>
        <p>
          This policy is written for the Canadian and United States version of the service. It
          follows PIPEDA, Quebec Law 25 and CalOPPA.
        </p>
      </Section>
      <Section title="What we collect">
        <p>
          We collect only what the service needs in order to make a book. There is no advertising,
          no profiling and no sale of personal information.
        </p>
        <ul>
          <li>
            Photographs of your child. You upload them yourself. They are the raw material for the
            illustrations.
          </li>
          <li>
            Voice recordings, if you choose to add narration. Depending on what you record, the
            recording may contain your child’s voice.
          </li>
          <li>
            Your child’s first name and age. They go into the story text so that the book is about
            your child and reads at the right level.
          </li>
          <li>
            Your email address. It identifies your account, confirms your registration and receives
            service messages.
          </li>
          <li>
            Your IP address and basic technical data about your browser. They are recorded for rate
            limiting, abuse response and error diagnosis.
          </li>
          <li>
            Records of your consent: the date, the version of the text you agreed to, and the
            technical details of that action.
          </li>
        </ul>
        <p>
          We strip EXIF metadata from uploaded photographs, so the location and camera data that
          phones normally embed in an image do not reach us or anyone downstream.
        </p>
        <p>
          We do not ask for and do not want your child’s surname, address, school, health
          information or any government identifier. Please do not put them into the story details.
        </p>
      </Section>
      <Section title="Why we collect it">
        <ul>
          <li>
            Photographs are used to generate illustrations that resemble your child, and the
            animated scenes built from those illustrations.
          </li>
          <li>Voice recordings are used to generate the narration of your book.</li>
          <li>Your child’s name and age are used to write the story text.</li>
          <li>
            Your email address is used to sign you in, to confirm your account, and to send messages
            about your books and your payments.
          </li>
          <li>
            IP addresses and technical data are used to keep the service running: enforcing quotas,
            blocking abuse and finding errors.
          </li>
        </ul>
        <p>
          We do not use your photographs, your voice recordings or your child’s data to train any
          model of our own, and we do not license them to anyone for that purpose.
        </p>
        <p>
          Our staff do not browse your photographs. Administrators of the service cannot see the
          images you upload; the system is built that way deliberately. Files are opened only when
          you or someone you gave a link to opens them, or when we are required to preserve material
          in response to a legal obligation described below.
        </p>
      </Section>
      <Section title="Who else receives your data">
        <p>
          Making a book requires artificial intelligence services that we do not operate. The full
          list of the companies that receive any part of your data is below. We remain accountable
          for what happens to your data at these providers, but we do not control their internal
          practices.
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="border border-slate-200 p-2 text-left font-semibold">
                  Subprocessor
                </th>
                <th className="border border-slate-200 p-2 text-left font-semibold">
                  What it receives
                </th>
                <th className="border border-slate-200 p-2 text-left font-semibold">Where</th>
                <th className="border border-slate-200 p-2 text-left font-semibold">Why</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-slate-200 p-2 align-top">
                  WaveSpeedAI PTE. LTD. / WaveSpeedAI LIMITED
                </td>
                <td className="border border-slate-200 p-2 align-top">
                  Photographs and voice recordings, as files
                </td>
                <td className="border border-slate-200 p-2 align-top">Singapore and Hong Kong</td>
                <td className="border border-slate-200 p-2 align-top">
                  Generating the illustrations, the animated scenes and the narration
                </td>
              </tr>
              <tr>
                <td className="border border-slate-200 p-2 align-top">OpenAI</td>
                <td className="border border-slate-200 p-2 align-top">
                  Story prompt text, which contains your child’s first name and age
                </td>
                <td className="border border-slate-200 p-2 align-top">United States</td>
                <td className="border border-slate-200 p-2 align-top">Writing the story</td>
              </tr>
              <tr>
                <td className="border border-slate-200 p-2 align-top">Anthropic</td>
                <td className="border border-slate-200 p-2 align-top">
                  The same story prompt text, when the service is configured to use it instead of
                  OpenAI
                </td>
                <td className="border border-slate-200 p-2 align-top">United States</td>
                <td className="border border-slate-200 p-2 align-top">Writing the story</td>
              </tr>
              <tr>
                <td className="border border-slate-200 p-2 align-top">OVHcloud</td>
                <td className="border border-slate-200 p-2 align-top">
                  Your account, your books and our records of your consent, in our database; files
                  pass through while a book is being made
                </td>
                <td className="border border-slate-200 p-2 align-top">
                  Beauharnois, Quebec, Canada
                </td>
                <td className="border border-slate-200 p-2 align-top">
                  Running the service: the server and the database
                </td>
              </tr>
              <tr>
                <td className="border border-slate-200 p-2 align-top">Cloudflare</td>
                <td className="border border-slate-200 p-2 align-top">
                  All files at rest: photographs, voice recordings, illustrations, video. Every
                  request to the site, including your IP address, passes through its network
                </td>
                <td className="border border-slate-200 p-2 align-top">
                  Files: North America, with no guarantee that they stay in Canada. Requests: the
                  Cloudflare location nearest to you
                </td>
                <td className="border border-slate-200 p-2 align-top">
                  Storing your files; protecting the site from attacks and delivering it
                </td>
              </tr>
              <tr>
                <td className="border border-slate-200 p-2 align-top">Resend</td>
                <td className="border border-slate-200 p-2 align-top">
                  Your email address and the contents of the messages we send you
                </td>
                <td className="border border-slate-200 p-2 align-top">United States</td>
                <td className="border border-slate-200 p-2 align-top">
                  Sending confirmation and service email
                </td>
              </tr>
              <tr>
                <td className="border border-slate-200 p-2 align-top">Sentry</td>
                <td className="border border-slate-200 p-2 align-top">
                  Error telemetry: technical error data, request URLs and IP address
                </td>
                <td className="border border-slate-200 p-2 align-top">
                  United States or European Union
                </td>
                <td className="border border-slate-200 p-2 align-top">
                  Diagnosing failures in the service
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          We do not sell personal information, we do not share it with advertisers, and we do not
          use analytics services that build a profile of you.
        </p>
      </Section>
      <Section title="Your data is processed outside Canada">
        <p>
          This is important enough to say on its own. Photographs of your child and any voice
          recordings are sent to Singapore and Hong Kong. Your child’s name and age are sent to the
          United States. This happens every time a book is generated. It is not optional: without it
          the service cannot produce anything.
        </p>
        <p>
          Our server and database are in Canada. The files themselves — photographs, voice
          recordings, illustrations and video — are stored with Cloudflare in North America. We ask
          for them to be kept in eastern North America, but Cloudflare does not offer a guarantee
          that they stay in Canada, so you should assume they may be stored in the United States.
          Your email address and the messages we send you are handled by Resend in the United
          States.
        </p>
        <p>
          While your data is in another country, it is subject to the laws of that country, and
          courts, law enforcement and regulators there may be able to obtain access to it under
          their own legal process. Canadian privacy law does not prevent this transfer, but it does
          require that we tell you about it clearly, and Quebec Law 25 requires that we assess the
          transfer before making it. We have.
        </p>
        <p>
          You give consent to this transfer separately from accepting our{' '}
          <Link to="/legal/terms">Terms of Service</Link>, before your first upload. You can
          withdraw that consent at any time.
        </p>
      </Section>
      <Section title="How long we keep it">
        <ul>
          <li>
            The photographs you upload are kept only until the drawing made from them is approved.
            From that moment every illustration, cover and export is made from the approved drawing,
            so the photographs are destroyed and never used again.
          </li>
          <li>
            If you upload photographs and never approve a drawing, they are destroyed 30 days after
            you last worked on that book. The book itself stays.
          </li>
          <li>
            Photographs of favorite things and places have no approval step: they are kept until 30
            days after you last worked on the book, and then destroyed. The drawings made from them
            stay.
          </li>
          <li>
            Voice recordings and the voice models made from them are kept while your account exists,
            because they are what your narrations are made of.
          </li>
          <li>
            Generated files — the drawings, illustrations and narrations — are kept while your
            account exists, because they are what your books are made of.
          </li>
          <li>
            When you archive a book, it leaves your library and its public link stops working. The
            drawings and text it is made of stay, because the book is what they are for; they go
            when you delete your account.
          </li>
          <li>
            When you delete your account, it enters a quarantine of {talearooLegal.quarantineTerm}.
            During that time nothing is visible and no one can sign in, and you can contact us to
            stop the process. After it the files are physically destroyed. What we keep is one
            record of the destruction itself — the address the account belonged to, the date, the
            kinds of file that were destroyed, the hashes of those files, and the address it last
            signed in from. It holds no photographs, no drawings and no names, and we keep it for{' '}
            {talearooLegal.destructionRecordTerm}, which is how long a complaint can arrive after
            the account is gone.
          </li>
          <li>
            Consent records and the account’s history of accepted document versions are kept after
            deletion, because they are the only proof of what you agreed to and when.
          </li>
          <li>
            Encrypted backups of our database are kept for {talearooLegal.backupRetentionTerm}.
            After you delete your account, your information can remain in them until they expire;
            they are used only to recover the service after a failure.
          </li>
          <li>
            Error telemetry is kept for a short retention period at Sentry and is not linked to your
            books.
          </li>
        </ul>
        <p>
          Why the photographs can go that early: a drawing is made once, and everything after it is
          made from the approved drawing. Keeping the photographs past that point would be keeping a
          picture of your child’s face with nothing left to do with it. The rule is the same for
          everyone, wherever you live.
        </p>
        <p>
          There are two honest exceptions to the above, and we would rather you learn about them
          here than later.
        </p>
        <p>
          First, we do not control how long our AI providers keep what we send them. WaveSpeed does
          not publish a retention period for uploaded files, and we have no way to delete anything
          on their side: they expose no such operation, and no copy of ours reaches it. When you
          delete your data, we delete it from our own systems, and that is the whole of what we can
          do. The honest answer to "is my child’s photograph gone everywhere" is: from our systems,
          yes; at the provider, no — what they received is theirs to hold, and we cannot say for how
          long.
        </p>
        <p>
          Second, we may be required by law to preserve data for longer, and in that case deletion
          is suspended for the affected data until the obligation ends. Canadian and United States
          law both require internet services to preserve material and related account data after
          reporting suspected child sexual abuse material to the authorities. If a legal hold
          applies to your account, deletion is paused for the period the law requires. We do not use
          legal holds for any other purpose.
        </p>
      </Section>
      <Section title="Public book links: read this before you share one">
        <p>
          You can publish a book to get a link that anyone can open without signing in. Only the
          finished illustrations are published. The photographs you uploaded are never made public.
        </p>
        <p>
          Unpublishing a book closes the link immediately: from that moment the address behaves
          exactly as if the book did not exist. But the address itself does not change. If you
          publish the same book again later, the old link starts working again, and anyone who saved
          or forwarded it will be able to open the book once more.
        </p>
        <p>
          If a link has spread further than you intended and you want a different address in its
          place, unpublish the book and create a copy to publish instead. The copy gets a new
          address of its own. The old address is not destroyed — it reopens if that book is ever
          published again — so if you want it dead for good, leave the original unpublished.
        </p>
        <p>
          Public links are also closed when an account is blocked for a violation of our{' '}
          <Link to="/legal/terms">Terms of Service</Link>, and when an account is deleted. In both
          cases the published books of that account stop being reachable.
        </p>
      </Section>
      <Section title="Your rights and how to use them">
        <p>
          You have the right to know what personal information we hold about you and to receive a
          copy of it, to correct it if it is wrong, to have it deleted, and to receive it in a
          structured, commonly used electronic format so that you can move it elsewhere. You also
          have the right to withdraw a consent you have given, including the consent to process your
          child’s photographs.
        </p>
        <p>
          To use any of these rights, write to <Mail address={privacy.email} /> from the email
          address on the account. We will answer within 30 days. If a request is complex enough that
          we need more time, we will tell you within those 30 days how much longer we need and why.
        </p>
        <p>
          Deleting your account and withdrawing photo consent can also be done directly in the
          service, without writing to us.
        </p>
        <p>
          If you are not satisfied with how we handled your request, you can complain to the Office
          of the Privacy Commissioner of Canada, and, if you live in Quebec, to the Commission
          d’accès à l’information du Québec. We would appreciate the chance to fix the problem
          first.
        </p>
      </Section>
      <Section title="Security">
        <p>
          All files are stored privately. There are no public addresses for uploaded photographs;
          when you view your own files, the service issues a temporary link that stops working after
          fifteen minutes. Passwords are stored hashed with Argon2id and are never readable,
          including by us. Registration requires a confirmed email address, so nothing can be
          uploaded anonymously.
        </p>
        <p>
          No system is perfect. If a breach of security safeguards happens that creates a real risk
          of significant harm, we will notify the Office of the Privacy Commissioner of Canada and
          the people affected as soon as feasible, and in Quebec the Commission d’accès à
          l’information. We keep a record of every security incident, including those that do not
          require notification.
        </p>
      </Section>
      <Section title="Children">
        <p>
          The service is for adults. An account holder must be 18 or older. Whoever uploads a
          photograph of a child states, at the upload itself, that they are that child’s parent or
          legal guardian or that they have the permission of the person shown. Children do not use
          the service and do not have accounts; the personal information about a child that we hold
          reaches us from an adult who consents on their behalf.
        </p>
        <p>
          If you believe a child has created an account or that someone has uploaded photographs of
          a child they are not responsible for, tell us at <Mail address={abuse.email} /> and we
          will act on it.
        </p>
      </Section>
      <Section title="Cookies">
        <p>
          We use a session cookie to keep you signed in. It is required for the service to work. We
          do not use advertising cookies, tracking pixels or third-party analytics.
        </p>
      </Section>
      <Section title="Changes to this policy">
        <p>
          This policy carries a version, which is the edition named at the top of this page. If we
          change what we collect, why we collect it or who receives it, we publish a new version
          and, where the change is significant, notify account holders by email. We will not apply a
          materially different use of data you have already given us without asking you again.
        </p>
      </Section>
    </>
  );
}

/**
 * Payment and refunds.
 *
 * The merchant of record rejects onboarding on a conditional refund policy — a conditional one
 * is itself grounds for refusal — so there is not a single condition here: no carve-out for
 * generations already spent and no discretion to refuse. The text is deliberately short,
 * because any attempt to describe exceptions turns an unconditional refund into a conditional
 * one and breaks onboarding.
 *
 * The economic problem — the cost is spent at the moment of generation — is answered by price
 * and quotas, not by the wording of the policy.
 */
function paymentAndRefunds(): ReactNode {
  return (
    <>
      <Section title="The policy">
        <p>
          If you are not satisfied with {talearooLegal.serviceName}, ask for your money back within
          30 days of the payment and we will refund it in full.
        </p>
        <p>
          This is unconditional. You do not have to explain why. It does not matter how many books
          you made or how many generations you used. We will not ask you to return anything, and the
          books you have already created remain yours.
        </p>
      </Section>
      <Section title="How to ask">
        <p>
          Write to <Mail address={support.email} /> from the email address on your account and say
          that you want a refund. One sentence is enough.
        </p>
        <p>
          We reply to confirm and start the refund at the same time. The money is returned to the
          card or account you paid from, and it usually appears within 5 to 10 business days,
          depending on your bank.
        </p>
      </Section>
      <Section title="Who processes the refund">
        <p>
          Payments for {talearooLegal.serviceName} are handled by Polar, which is the merchant of
          record for the sale. The refund is processed through Polar and is returned to the payment
          method you used. Any tax charged at the time of the sale is returned with it.
        </p>
      </Section>
      <Section title="Subscriptions">
        <p>
          You can cancel a subscription at any time from your account. Cancelling stops future
          charges immediately and you keep access until the end of the period you have paid for.
        </p>
        <p>
          A cancellation and a refund are different things. If you also want the last payment back,
          ask for a refund under this policy and you will get it.
        </p>
      </Section>
      <Section title="Your legal rights">
        <p>
          This policy is in addition to the rights that consumer protection law gives you. It does
          not replace them and it does not take anything away from them.
        </p>
      </Section>
    </>
  );
}

/**
 * Express consent to process photographs of the people in a book.
 *
 * Shown next to a separate checkbox before the first upload — a separate act, not inside "I
 * accept the terms": a photograph of a person — usually a child — plus a name and an age, sent
 * to an AI provider in Singapore, is sensitive information, and Quebec Law 25 requires express
 * consent in a step of its own.
 *
 * Written for anyone in the photographs, not only "your child", since 2 October 2026: a book's
 * characters can be the parent, a grandparent or a friend, and the checkbox already says
 * "everyone in the photos I upload is me, my child, or someone who agreed to it". A consent
 * text that covers only children leaves the adults in the same upload with no consent at all.
 * Children stay named where the law treats them differently (who may consent for them).
 *
 * The structure follows the OPC Guidelines for Obtaining Meaningful Consent, which name four
 * elements needing prominence: what data, which third parties — named, not "partners" — the
 * purposes in plain language, and the risk of harm. The forthrightness about WaveSpeed's
 * undisclosed retention comes from the same place: promising a deletion we do not control
 * would obtain consent by deception.
 *
 * The biometric wording is deliberately two steps and not one. First what we do: no face
 * recognition, no geometry measured, no template built. Then what leaves: several frames of one
 * face into an editing model whose job is to preserve likeness. The first step
 * alone would be true and not honest — a reader would conclude that photographs sit outside
 * the biometric laws, while Monroy v. Shutterfly and Rivera v. Google read the photograph
 * exclusion in BIPA §10 narrowly: it protects storing a picture, not what is extracted from
 * it. So the text expressly declines to claim the law does not reach us.
 */
function photoConsent({ Link }: LegalRenderContext): ReactNode {
  return (
    <>
      <Section title="What happens to the photographs">
        <p>
          To draw the people in your book as characters — your child, you, a grandparent, anyone you
          add — we send the photographs you upload to an artificial intelligence service run by
          another company: WaveSpeedAI, based in Singapore and Hong Kong. If you record narration,
          the recording is sent to the same company, and the voice of anyone who speaks in it is
          sent too.
        </p>
        <p>
          The first name and age you give each character are sent separately, as part of the story
          text, to OpenAI or Anthropic in the United States.
        </p>
        <p>
          The photographs themselves are stored with Cloudflare, in North America but not
          necessarily in Canada, until they are destroyed as described below.
        </p>
        <p>
          This means the photographs, voices and names of the people in your book are processed
          outside Canada, under the laws of Singapore, Hong Kong and the United States rather than
          Canadian law.
        </p>
      </Section>
      <Section title="What they are used for">
        <p>
          The photographs are used for one thing: creating the illustrations, animated scenes and
          narration of your book. The names and ages are used to write the story so that it is about
          the people in it and reads at the right age.
        </p>
        <p>
          We do not use these photographs to train any model of our own. No one on our team looks at
          them; administrators of the service cannot open the images you upload. We do not sell
          them, publish them or show them to anyone else. When you publish a book, what becomes
          public is the drawn illustration, never the photograph it was made from.
        </p>
        <p>
          We do not run face recognition on the photographs. We do not measure the geometry of
          anyone’s face, we do not build a faceprint or any other template from it, and we do not
          compare a face against any other photograph or database. That is what happens on our side.
        </p>
        <p>
          What we send to the illustration service is different. To draw a person as a character we
          send their photographs — each one as its own file — to an image model whose task is to
          keep them recognisable from page to page. Whether that model measures their face inside
          its own systems in order to do this is not something we can inspect or promise either way.
        </p>
        <p>
          The law of several US states, Illinois in particular, treats a measurement taken from a
          face as biometric data, while a photograph on its own is not. Courts there have held that
          this distinction protects the storing of a picture, not what is extracted from it. We are
          not going to tell you that this places these photographs outside those laws, because we do
          not think anyone can honestly say that about a service like ours. We would rather you
          decide knowing that.
        </p>
      </Section>
      <Section title="What we cannot promise">
        <p>
          WaveSpeed does not publish how long it keeps the files we send it, and it has not
          committed to telling us if its systems are breached. So we can tell you honestly what we
          do, and not what happens on their side. If you ask us to delete your data, we delete it
          from our systems. We cannot delete it at WaveSpeed: they expose no operation for that, so
          the copy they received stays theirs to hold.
        </p>
        <p>
          The risk you are accepting is this: a photograph of your child, or of anyone in your book,
          leaves our control and is held by a company in another country for an unknown period,
          where a security failure or a legal demand could expose it. We think the risk is small,
          and we have limited it as far as we can, but it is real and you should decide with it in
          mind.
        </p>
      </Section>
      <Section title="How long we keep the photographs">
        <ul>
          <li>
            Photographs are kept only until the drawing made from them is approved. From that moment
            every illustration, cover and export is made from the approved drawing, so the
            photographs are destroyed and never used again.
          </li>
          <li>
            If you upload photographs and never approve a drawing, they are destroyed 30 days after
            you last worked on that book. The book itself stays.
          </li>
          <li>
            Photographs of favorite things and places have no approval step: they are kept until 30
            days after you last worked on the book, and then destroyed. The drawings made from them
            stay.
          </li>
          <li>
            If you change the style later, the drawing is made again from the photographs — so you
            upload them again. That is the trade we have chosen: we do not keep a picture of anyone
            on file against the possibility.
          </li>
          <li>
            A legal hold postpones destruction — the law imposes one on us in the situations
            described in the privacy policy, and only for as long as that obligation lasts. It is
            not the only thing that does: a photograph stays while another part of your library
            still uses it, and while a drawing that was given it is still being made.
          </li>
        </ul>
        <p>
          This schedule is published because we are required to publish it, and it applies to
          everyone regardless of where they live.
        </p>
      </Section>
      <Section title="You can change your mind">
        <p>
          You can withdraw this consent at any time in your account settings, in one action. After
          that we stop sending anything about the people in your book to these services, and you can
          delete the uploaded photographs and your account whenever you want. Withdrawing consent
          does not affect books that have already been generated, which stay yours.
        </p>
        <p>
          If you would rather write to a person, or you want to ask something before you decide,
          contact <Mail address={privacy.email} />. Full details are in our{' '}
          <Link to="/legal/privacy">Privacy Policy</Link>.
        </p>
      </Section>
      <Section title="What you are confirming">
        <ul>
          <li>
            Everyone in the photographs I upload is me, a child I am the parent or legal guardian
            of, or someone who has agreed to their photographs being used this way — and for a child
            who is not mine, their parent or guardian has agreed.
          </li>
          <li>
            I agree that these photographs, and any voice recording I upload, may be sent to
            WaveSpeedAI in Singapore and Hong Kong, and that the first names and ages I give may be
            sent to OpenAI or Anthropic in the United States, in order to create my book.
          </li>
          <li>
            I understand that this processing happens outside Canada, that the photographs are
            stored with Cloudflare in North America and possibly in the United States until they are
            destroyed, and that WaveSpeedAI, OpenAI and Anthropic do not disclose how long they keep
            what they receive.
          </li>
          <li>
            I understand that the photographs of a person are destroyed as soon as the drawing made
            from them is approved, or 30 days after I last worked on the book if I never approve
            one, and that photographs of favorite things and places are destroyed 30 days after I
            last worked on the book.
          </li>
        </ul>
      </Section>
    </>
  );
}

/**
 * Consent to record and clone a voice.
 *
 * A separate document rather than a paragraph in the photo consent, for two reasons.
 *
 * The first is legal. Illinois BIPA §10 lists `voiceprint` explicitly, with no purpose
 * qualifier and no exclusion comparable to the one that takes photographs out of the
 * definition. A voice model built from a sample is a biometric identifier literally, regardless
 * of our using it to narrate a bedtime story rather than to open a door. The §15(b)
 * requirements are requirements about the content of the notice: name what is collected, why,
 * and for how long. Hence the structure below.
 *
 * The second is factual. The account holder gives the consent, but the voice on the recording
 * is usually someone else's — a mother, a grandmother, an older brother. One adult is agreeing
 * on behalf of another, and the text has to say so outright, or the consent comes from someone
 * who has no standing to give it.
 */
function voiceConsent(): ReactNode {
  return (
    <>
      <Section title="What we build from the recording">
        <p>
          When you upload a voice sample, we send it to WaveSpeedAI, a company based in Singapore
          and Hong Kong, which builds a voice model from it. That model is what reads your book
          aloud in that voice.
        </p>
        <p>
          The recording itself is stored with Cloudflare, in North America but not necessarily in
          Canada, until it is destroyed as described below.
        </p>
        <p>
          A voice model of this kind is called a voiceprint. Under the law of several US states —
          Illinois in particular — a voiceprint is biometric data, and it stays biometric data even
          though we only use it to narrate a children’s book and never to identify anyone. That is
          why this consent is separate from every other one on the site.
        </p>
        <p>
          We do not use the voiceprint to recognise, verify or identify a person, we do not compare
          it against any other recording, and we never sell, lease, trade or otherwise profit from
          it.
        </p>
      </Section>
      <Section title="Whose voice this has to be">
        <p>
          You may only upload a recording of your own voice, or the voice of an adult who has agreed
          to this specific use after reading this page. In practice that is usually a parent or a
          grandparent recording a book for a child.
        </p>
        <p>
          Please do not upload a recording of a child’s voice, and do not upload the voice of a
          public figure, a performer, or anyone you found online. A voice belongs to the person who
          speaks with it.
        </p>
      </Section>
      <Section title="How long we keep it and when we destroy it">
        <ul>
          <li>
            The voice sample and the voice model are kept while you keep using your account, because
            they are what your narration is made of.
          </li>
          <li>
            The recording is destroyed when you delete the voice or when you delete your account —
            within a month at the latest, because that is the pass the files are collected on.
            Narration you have already generated stays in your books.
          </li>
          <li>
            A legal hold postpones destruction — the law imposes one on us in the situations
            described in the privacy policy, and only for as long as that obligation lasts. It is
            not the only thing that does: a photograph stays while another part of your library
            still uses it, and while a drawing that was given it is still being made.
          </li>
        </ul>
        <p>
          This schedule is published because we are required to publish it, and it applies to
          everyone regardless of where they live.
        </p>
      </Section>
      <Section title="You can change your mind">
        <p>
          You can delete a voice at any time, and you can delete your account at any time. We then
          delete the sample from our storage, and the recording stops being used. The model built
          from it at WaveSpeed is not ours to delete — they expose no operation for that, and we
          cannot verify what they keep. So we can tell you honestly what we did on our side and not
          what happened on theirs.
        </p>
        <p>
          If you would like to ask something before deciding, write to{' '}
          <Mail address={privacy.email} />.
        </p>
      </Section>
      <Section title="What you are confirming">
        <ul>
          <li>
            This is my own voice, or the voice of an adult who has read this page and agreed to it.
          </li>
          <li>
            I agree that the recording may be sent to WaveSpeedAI in Singapore and Hong Kong, and
            that a voiceprint may be created from it in order to narrate my book.
          </li>
          <li>
            I understand that the recording is stored with Cloudflare in North America, possibly in
            the United States, until it is destroyed.
          </li>
          <li>
            I understand that the recording and the voiceprint are destroyed when I delete the voice
            or my account, within a month at the latest.
          </li>
        </ul>
      </Section>
    </>
  );
}

/**
 * Publication is the one consent whose subject is other people seeing the child, rather than a
 * provider processing them — so it is asked for at the moment the link is created and kept
 * separate from the rest. It was the last of the three still pointing at the privacy policy,
 * where the checkbox's "Consent text" link landed on a document that says nothing about what a
 * public link is or how to close it.
 */
function publicationConsent({ Link }: LegalRenderContext): ReactNode {
  return (
    <>
      <Section title="What publishing does">
        <p>
          Publishing a book creates a link that opens without signing in. Anyone holding it can read
          the book and pass it on, which is the point — it is how a grandparent opens the book
          without an account of their own.
        </p>
        <p>
          The link is long and unguessable, but it is not a secret. It can be forwarded, pasted into
          a message, or left in a browser history. Treat it as something you have handed out rather
          than something you control.
        </p>
      </Section>
      <Section title="What becomes visible">
        <ul>
          <li>the title and the whole text of the book;</li>
          <li>the names and ages you gave the characters;</li>
          <li>the illustrations drawn from your photographs, and the cover;</li>
          <li>the animated scenes, if the book has them;</li>
          <li>the narration, in whichever voice you chose.</li>
        </ul>
        <p>
          The photographs you uploaded are not published, and neither is the voice recording. What a
          reader sees is the drawing made from a photograph, never the photograph itself.
        </p>
      </Section>
      <Section title="What you are stating about other people">
        <p>
          A published book shows a child, and often names them. By publishing you state that you are
          that child’s parent or legal guardian, and that anyone else recognisable in the book — an
          adult character, a voice — has agreed to this.
        </p>
        <p>
          If a book is published without the agreement of someone in it, tell us at{' '}
          <Mail address={abuse.email} /> and we will take it down. That is also how anyone who finds
          such a book can reach us: the address is on every published page.
        </p>
      </Section>
      <Section title="Stopping it">
        <p>
          You can unpublish at any time from the Export step, and the link stops working
          immediately. Deleting your account closes it too, as does our blocking the account.
        </p>
        <p>
          What we cannot do is reach a copy someone already made. A reader can screenshot a page or
          save the file while the link is live, and after that the copy is theirs. Unpublishing ends
          the access we control and nothing else, so it is worth deciding who gets the link before
          sending it rather than after.
        </p>
        <p>
          How long the book itself is kept, and what happens to it when you close your account, is
          in the <Link to="/legal/privacy">Privacy Policy</Link>.
        </p>
      </Section>
      <Section title="What you are confirming">
        <ul>
          <li>
            I am the parent or legal guardian of the child in this book, and anyone else
            recognisable in it has agreed to it being published.
          </li>
          <li>
            I understand the link opens without signing in, and that whoever receives it can pass it
            on.
          </li>
          <li>
            I understand that unpublishing stops the link but cannot recall a copy already made.
          </li>
        </ul>
      </Section>
    </>
  );
}

export const talearooDocuments: readonly LegalDocument[] = [
  {
    id: 'terms',
    title: 'Terms of Service',
    summary:
      'The agreement between you and TaleAroo: what the service does, what you promise about the photographs you upload, what you pay, and what each of us is responsible for.',
    render: termsOfService,
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    summary:
      'What TaleAroo collects, who it is sent to, where in the world it goes, how long it is kept, and how you can get it back or have it deleted.',
    render: privacyPolicy,
  },
  {
    id: 'payment',
    title: 'Payment and refunds',
    pageTitle: 'Payment and Refund Policy',
    summary: 'If you are not happy with what you paid for, ask and we refund you. No conditions.',
    render: paymentAndRefunds,
  },
  {
    id: 'photo-consent',
    title: 'Consent to process your child’s photographs',
    summary:
      'Please read this before you upload. It explains where your child’s photographs go and who else sees them.',
    render: photoConsent,
  },
  {
    id: 'voice-consent',
    title: 'Consent to record and clone a voice',
    summary:
      'Please read this before you upload a voice recording. A cloned voice is biometric data under the law, and the person whose voice it is has to agree.',
    render: voiceConsent,
  },
  {
    id: 'publication-consent',
    title: 'Consent to publish a book by link',
    summary:
      'What a public link shows, who can open it, and what unpublishing can and cannot take back.',
    render: publicationConsent,
  },
];
